import React, { useState } from "react";
import "./index.scss";

import { tintFor } from "../appShell";

// ---------------------------------------------------------------------------
// Avatar
// ---------------------------------------------------------------------------
// A rounded square. A photo is cropped to fill; a team logo is transparent
// artwork so it is contained on white with a hairline instead, never cropped.
// A broken URL falls back to initials on the tint the name always maps to.
export function Avatar({ name, src, size = 32, logo = false }) {
  const [broken, setBroken] = useState(false);
  const label = String(name || "?");
  const initials = label
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  const usable = src && src !== "undefined" && src !== "null" && !broken;

  return (
    <span
      className={`au-avatar ${logo ? "logo" : ""}`}
      style={{
        width: size,
        height: size,
        background: usable && logo ? "#fff" : usable ? "transparent" : tintFor(label),
        fontSize: Math.round(size * 0.38),
      }}
      title={label}
    >
      {usable ? (
        <img src={src} alt="" onError={() => setBroken(true)} />
      ) : (
        initials || "?"
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Pill
// ---------------------------------------------------------------------------
// `tone` names the meaning, not the colour. "quiet" is the outline a "no"
// gets, so an absent value still reads as a value rather than as nothing.
export function Pill({ tone = "neutral", children, style }) {
  return (
    <span className={`au-pill ${tone}`} style={style}>
      {children}
    </span>
  );
}

// Player role is free text, so it is tinted by keyword with a neutral fallback
// rather than dropped when the text is something the app has not seen.
export function RolePill({ role }) {
  const key = String(role || "").toLowerCase();
  let tone = "neutral";
  if (key.includes("bat")) tone = "warn";
  else if (key.includes("bowl")) tone = "danger";
  else if (key.includes("all")) tone = "success";
  else if (key.includes("keep") || key.includes("wk")) tone = "primary";

  return <Pill tone={tone}>{role || "—"}</Pill>;
}

// A player's 0-100 rating. The whole men's pool is unrated, so an absent or
// zero rating renders nothing at all rather than a misleading "0". The band is
// coarse on purpose: it says strong / solid / rest, not a precise ranking.
export function RatingBadge({ value }) {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n) || n <= 0) return null;

  const tone = n >= 85 ? "success" : n >= 70 ? "primary" : "neutral";
  return (
    <Pill tone={tone}>
      <span className="au-rating-label">Rating</span> {n}
    </Pill>
  );
}

// SOLD / UNSOLD / ACTIVE. Sold and live are solid because they are the "yes";
// unsold is the quiet outline.
export function StatusPill({ player }) {
  if (player?.isSold) return <Pill tone="success solid">Sold</Pill>;
  if (player?.active) return <Pill tone="primary solid">Live</Pill>;
  return <Pill tone="quiet">Unsold</Pill>;
}

// ---------------------------------------------------------------------------
// Overseas marker
// ---------------------------------------------------------------------------
// This used to be a small aeroplane PNG beside the name. A globe says
// "overseas" without implying travel, and being drawn inline it takes the
// colour of whatever contains it.
export function OverseasMark({ nationality }) {
  const home = String(nationality || "").toLowerCase();
  if (!nationality || home === "india" || home === "indian") return null;

  return (
    <svg
      className="au-overseas"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <title>Overseas</title>
      <circle cx="12" cy="12" r="8.6" />
      <path d="M3.4 12h17.2" />
      <path d="M12 3.4a13 13 0 0 1 0 17.2 13 13 0 0 1 0-17.2" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------
// Lakhs below a crore, crores above, which is how the numbers are read aloud
// in the room. `dash` keeps an empty cell from collapsing.
export function money(value) {
  if (value === 0) return "0 L";
  if (!value) return "—";
  return value > 99 ? `${+(value / 100).toFixed(2)} CR` : `${value} L`;
}

// "Rohit Sharma" reads as "R. Sharma" in a dense table, but a single-word name
// has no surname to shorten to and is left alone.
export function shortName(full) {
  const parts = String(full || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "—";
  if (parts.length === 1) return parts[0];
  return `${parts[0][0]}. ${parts.slice(1).join(" ")}`;
}

// ---------------------------------------------------------------------------
// Optional fields
// ---------------------------------------------------------------------------
// The men's pool has no ratings and no mentality, and a keeper has no bowling
// style, so most player attributes are optional. The source sheet writes an
// absent value as "NA", which would otherwise render as a real answer.
const ABSENT = new Set(["", "na", "n/a", "n.a.", "-", "--", "none", "null", "undefined"]);

export function present(value) {
  if (value === undefined || value === null) return false;
  return !ABSENT.has(String(value).trim().toLowerCase());
}
